import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Archive, CalendarDays, ChevronLeft, ChevronRight, Edit3, Plus, Search, Trash2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './AgendamentosConsultorio.css';

const TITULOS = {
  medico: 'Médico',
  odontologia: 'Odontologia',
};

const DIAS = [
  ['segunda', 'Segunda-feira'],
  ['terca', 'Terça-feira'],
  ['quarta', 'Quarta-feira'],
  ['quinta', 'Quinta-feira'],
  ['sexta', 'Sexta-feira'],
  ['sabado', 'Sábado'],
];

const CORES = [
  '#11c9d3',
  '#ef9bb5',
  '#9eb0c5',
  '#ef1717',
  '#e8c8c8',
  '#40b4b0',
];

const pad = (value) => String(value).padStart(2, '0');

const mesAtual = () => {
  const data = new Date();
  return `${data.getFullYear()}-${pad(data.getMonth() + 1)}`;
};

const formatarMes = (mes) => {
  const [ano, numero] = mes.split('-').map(Number);
  return new Date(ano, numero - 1, 1).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  }).replace(/^./, (letra) => letra.toUpperCase());
};

const alterarMes = (mes, quantidade) => {
  const [ano, numero] = mes.split('-').map(Number);
  const data = new Date(ano, numero - 1 + quantidade, 1);
  return `${data.getFullYear()}-${pad(data.getMonth() + 1)}`;
};

const datasDoMes = (mes) => {
  const [ano, numero] = mes.split('-').map(Number);
  const ultimoDia = new Date(ano, numero, 0).getDate();

  return Array.from({ length: ultimoDia }, (_, index) => {
    const data = new Date(ano, numero - 1, index + 1);
    const semana = data.getDay();

    if (semana === 0) return null;

    const nomes = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];

    return {
      iso: `${ano}-${pad(numero)}-${pad(index + 1)}`,
      dia: index + 1,
      semana: nomes[semana],
    };
  }).filter(Boolean);
};

async function api(url, options = {}) {
  const resposta = await fetch(`/api${url}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });

  const texto = await resposta.text();
  let dados = {};

  try {
    dados = texto ? JSON.parse(texto) : {};
  } catch (_) {
    dados = { error: texto };
  }

  if (!resposta.ok) {
    throw new Error(dados.error || `Erro HTTP ${resposta.status}`);
  }

  return dados;
}

function nomeProfissional(agenda) {
  return agenda.medico_nome || agenda.nome || 'Profissional';
}

function AgendaBloco({ agenda, setor, onAbrir }) {
  const horarios = Array.isArray(agenda.horarios) ? agenda.horarios : [];
  const preenchidos = horarios.filter((item) => item.paciente || item.status === 'confirmado');

  const texto = preenchidos.length
    ? preenchidos
      .slice(0, 3)
      .map((item) => `${item.horario || ''} ${item.paciente || ''}`.trim())
      .join('\n')
    : `${agenda.hora_inicio || ''}–${agenda.hora_fim || ''} ${nomeProfissional(agenda)}`;

  const cor = agenda.cor
    || horarios.find((item) => item.cor)?.cor
    || '#11c9d3';

  return (
    <button
      type="button"
      className="consultorio-agenda-bloco"
      style={{ '--consultorio-cor': cor }}
      aria-disabled="true"
      title={nomeProfissional(agenda)}
    >
      <strong>{agenda.data ? agenda.data.slice(8, 10) : ''}/
        {agenda.data ? agenda.data.slice(5, 7) : ''}
        {' '}{nomeProfissional(agenda)}
      </strong>
      <span>{texto}</span>
      <Edit3 size={13} className="consultorio-agenda-editar" />
    </button>
  );
}

// Seletor de horário sempre em 24h (o input type="time" segue o idioma do sistema e pode mostrar AM/PM).
function HoraInput24({ value, onChange }) {
  const texto = String(value || '');
  const valido = /^\d{1,2}:\d{2}/.test(texto);
  const hora = valido ? texto.split(':')[0].padStart(2, '0') : '';
  const minuto = valido ? texto.split(':')[1].slice(0, 2) : '';
  const horas = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
  const base = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));
  const minutos = minuto && !base.includes(minuto) ? [...base, minuto].sort() : base;
  const mudar = (novaHora, novoMinuto) => onChange(`${novaHora || '00'}:${novoMinuto || '00'}`);
  return (
    <span className="hora24">
      <select aria-label="Hora" value={hora} onChange={(e) => mudar(e.target.value, minuto)}>
        {!hora && <option value="">--</option>}
        {horas.map((h) => <option key={h} value={h}>{h}</option>)}
      </select>
      <span className="hora24-sep">:</span>
      <select aria-label="Minuto" value={minuto} onChange={(e) => mudar(hora, e.target.value)}>
        {!minuto && <option value="">--</option>}
        {minutos.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
      <span className="hora24-sufixo">h</span>
    </span>
  );
}

export default function AgendamentosConsultorio({ setor }) {
  const navigate = useNavigate();
  const [mes, setMes] = useState(mesAtual);
  const [dados, setDados] = useState({ consultorios: [] });
  const [medicos, setMedicos] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [consultorioVinculo, setConsultorioVinculo] = useState(null);
  const [medicoVinculo, setMedicoVinculo] = useState('');
  const [salvandoVinculo, setSalvandoVinculo] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [novoConsultorio, setNovoConsultorio] = useState(false);
  const [nomeNovo, setNomeNovo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [novaRapida, setNovaRapida] = useState(null);
  const [formRapida, setFormRapida] = useState({ medicoId: '', consultorioId: '', datas: [], inicio: '09:00', fim: '17:00', intervalo: 20 });
  const [conflitos, setConflitos] = useState([]);
  const [erroRapida, setErroRapida] = useState('');
  const [salvandoRapida, setSalvandoRapida] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setCarregando(true);
      const resposta = await api(
        `/consultorios/grade?setor=${encodeURIComponent(setor)}&mes=${mes}`
      );
      setDados(resposta);
      setErro('');
    } catch (error) {
      setErro(error.message);
    } finally {
      setCarregando(false);
    }
  }, [setor, mes]);

  useEffect(() => {
    carregar();

    api(`/medicos?setor=${encodeURIComponent(setor)}`)
      .then((lista) => setMedicos(Array.isArray(lista) ? lista : []))
      .catch((error) => setErro(error.message));
  }, [carregar, setor]);

  const dias = useMemo(() => datasDoMes(mes), [mes]);

  const diasVisiveis = dias.filter((dia) =>
    ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'].includes(dia.semana)
  );

  const abrirAgenda = (agenda) => {
    const chave = `${agenda.medicoId}::${agenda.especialidade || agenda.nome || 'Agenda'}`;
    const parametros = new URLSearchParams({
      agenda: chave,
      agendaId: String(agenda.id),
      dia: String(agenda.data || '').slice(0, 10),
    });

    navigate(`/agendamentos/${setor}?${parametros.toString()}`);
  };

  const abrirVinculoMedico = (consultorio) => {
    setConsultorioVinculo(consultorio);
    setMedicoVinculo('');
  };

  const salvarVinculoMedico = async (event) => {
    event.preventDefault();

    if (!consultorioVinculo || !medicoVinculo) return;

    try {
      setSalvandoVinculo(true);

      await api(`/consultorios/${consultorioVinculo.id}/medico`, {
        method: 'PUT',
        body: JSON.stringify({
          setor,
          medico_id: Number(medicoVinculo),
        }),
      });

      setConsultorioVinculo(null);
      setMedicoVinculo('');
      await carregar();
    } catch (error) {
      setErro(error.message);
    } finally {
      setSalvandoVinculo(false);
    }
  };

  const salvarConsultorio = async (event) => {
    event.preventDefault();

    if (!nomeNovo.trim()) return;

    try {
      setSalvando(true);

      await api('/consultorios', {
        method: 'POST',
        body: JSON.stringify({
          setor,
          nome: nomeNovo.trim(),
        }),
      });

      setNomeNovo('');
      setNovoConsultorio(false);
      await carregar();
    } catch (error) {
      setErro(error.message);
    } finally {
      setSalvando(false);
    }
  };

  const paraMinutos = (valor) => {
    const partes = /^(\d{1,2}):(\d{2})/.exec(String(valor || ''));
    return partes ? Number(partes[1]) * 60 + Number(partes[2]) : null;
  };

  const datasDoDiaRapida = novaRapida
    ? dias.filter((dia) => dia.semana === novaRapida.dia).map((dia) => dia.iso)
    : [];

  const abrirNovaRapida = (diaSemana, consultorio = null) => {
    setNovaRapida({ dia: diaSemana });
    setFormRapida({
      medicoId: '',
      consultorioId: consultorio ? String(consultorio.id) : '',
      datas: [],
      inicio: '09:00',
      fim: '17:00',
      intervalo: 20,
    });
    setConflitos([]);
    setErroRapida('');
  };

  const fecharNovaRapida = () => {
    if (salvandoRapida) return;
    setNovaRapida(null);
    setConflitos([]);
    setErroRapida('');
  };

  const alternarDataRapida = (iso) => {
    setConflitos([]);
    setFormRapida((atual) => ({
      ...atual,
      datas: atual.datas.includes(iso) ? atual.datas.filter((d) => d !== iso) : [...atual.datas, iso].sort(),
    }));
  };

  const salvarNovaRapida = async (event) => {
    event.preventDefault();
    const { medicoId, consultorioId, datas, inicio, fim, intervalo } = formRapida;
    setErroRapida('');
    setConflitos([]);

    if (!medicoId || !consultorioId || !datas.length) {
      setErroRapida('Escolha o profissional, o consultório e pelo menos uma data.');
      return;
    }
    const minInicio = paraMinutos(inicio);
    const minFim = paraMinutos(fim);
    if (minInicio === null || minFim === null || minFim <= minInicio) {
      setErroRapida('O horário final precisa ser depois do horário inicial.');
      return;
    }

    setSalvandoRapida(true);
    try {
      // Agendas existentes: grade (com consultório) + lista geral do setor (inclui as sem consultório)
      const [ano, numeroMes] = mes.split('-').map(Number);
      const ultimoDia = new Date(ano, numeroMes, 0).getDate();
      let doResumo = [];
      try {
        const resposta = await api(`/agenda-resumo?de=${mes}-01&ate=${mes}-${pad(ultimoDia)}&setor=${encodeURIComponent(setor)}&estado=ativa`);
        doResumo = Array.isArray(resposta) ? resposta : [];
      } catch (_) { doResumo = []; }
      const daGrade = (dados.consultorios || []).flatMap((consultorio) =>
        (consultorio.agendas || []).map((agenda) => ({ ...agenda, consultorio_id: consultorio.id }))
      );
      const porId = new Map();
      [...doResumo, ...daGrade].forEach((agenda) => {
        if (!agenda || agenda.id == null) return;
        porId.set(String(agenda.id), { ...(porId.get(String(agenda.id)) || {}), ...agenda });
      });
      const existentes = Array.from(porId.values());

      const nomeDoMedico = (id) => medicos.find((m) => String(m.id) === String(id))?.nome || 'Profissional';
      const nomeDoConsultorio = (id) => (dados.consultorios || []).find((c) => String(c.id) === String(id))?.nome || 'Consultório';

      const encontrados = [];
      datas.forEach((data) => {
        existentes.forEach((agenda) => {
          if (String(agenda.data || '').slice(0, 10) !== data) return;
          if (String(agenda.estado || 'ativa') !== 'ativa') return;
          const ini = paraMinutos(agenda.hora_inicio) ?? 0;
          const fimExistente = paraMinutos(agenda.hora_fim) ?? 24 * 60;
          if (!(ini < minFim && minInicio < fimExistente)) return;
          const mesmoConsultorio = String(agenda.consultorio_id ?? '') === String(consultorioId);
          const mesmoMedico = String(agenda.medicoId ?? agenda.medico_id ?? '') === String(medicoId);
          if (!mesmoConsultorio && !mesmoMedico) return;
          encontrados.push({
            data,
            motivo: mesmoConsultorio ? `${nomeDoConsultorio(consultorioId)} ocupado` : 'Profissional já tem agenda',
            quem: agenda.medico_nome || nomeDoMedico(agenda.medicoId ?? agenda.medico_id),
            horario: `${agenda.hora_inicio || '?'}–${agenda.hora_fim || '?'}`,
            local: mesmoConsultorio ? '' : (agenda.consultorio_id ? ` em ${nomeDoConsultorio(agenda.consultorio_id)}` : ''),
          });
        });
      });

      if (encontrados.length) {
        setConflitos(encontrados);
        return;
      }

      const medico = medicos.find((m) => String(m.id) === String(medicoId));
      const especialidadeMedico =
        (medico && typeof medico.especialidade === 'object' && medico.especialidade?.nome)
        || (medico && typeof medico.especialidade === 'string' && medico.especialidade)
        || medico?.especialidade_nome
        || existentes.find((a) => String(a.medicoId ?? a.medico_id) === String(medicoId) && a.especialidade)?.especialidade
        || '';

      for (const data of [...datas].sort()) {
        const criada = await api('/agenda-configuracoes', {
          method: 'POST',
          body: JSON.stringify({
            medicoId: Number(medicoId),
            nome: `Agenda - ${medico?.nome || ''}`,
            especialidade: especialidadeMedico,
            data,
            hora_inicio: inicio,
            hora_fim: fim,
            intervalo: Number(intervalo),
            modalidade: setor === 'odontologia' ? 'Agenda odontológica' : 'Agenda médica',
            dia_clinica: new Date(`${data}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'long' }),
            consultorio_id: Number(consultorioId),
          }),
        });
        await api(`/agenda-configuracoes/${criada.id}/horarios/gerar`, {
          method: 'POST',
          body: JSON.stringify({ inicio, fim, intervalo: Number(intervalo) }),
        });
      }

      setNovaRapida(null);
      await carregar();
    } catch (error) {
      setErroRapida(error.message || 'Não foi possível criar a agenda.');
    } finally {
      setSalvandoRapida(false);
    }
  };

  const consultorios = (dados.consultorios || []).filter((consultorio) =>
    !filtro.trim()
      || consultorio.nome.toLowerCase().includes(filtro.trim().toLowerCase())
      || consultorio.agendas.some((agenda) =>
        `${nomeProfissional(agenda)} ${agenda.especialidade || ''}`
          .toLowerCase()
          .includes(filtro.trim().toLowerCase())
      )
  );

  return (
    <main className="consultorio-page">
      <header className="consultorio-header">
        <div>
          <span className="consultorio-kicker">Central de agendas</span>
          <h1><CalendarDays size={24} /> Agendamentos · {TITULOS[setor]}</h1>
          <p>Organize profissionais e horários por consultório.</p>
        </div>

        <div className="consultorio-header-actions">
          <button
            type="button"
            className="consultorio-button consultorio-button-light"
            onClick={() => navigate(`/agendamentos/${setor}`)}
          >
            Voltar à agenda
          </button>

          <button
            type="button"
            className="consultorio-button consultorio-button-primary"
            onClick={() => setNovoConsultorio(true)}
          >
            <Plus size={16} /> Novo consultório
          </button>
        </div>
      </header>

      <section className="consultorio-toolbar">
        <button type="button" onClick={() => setMes((atual) => alterarMes(atual, -1))}>
          <ChevronLeft size={17} />
        </button>

        <strong>{formatarMes(mes)}</strong>

        <button type="button" onClick={() => setMes((atual) => alterarMes(atual, 1))}>
          <ChevronRight size={17} />
        </button>

        <button
          type="button"
          className="consultorio-hoje"
          onClick={() => setMes(mesAtual())}
        >
          Hoje
        </button>

        <label className="consultorio-filtro">
          <Search size={15} />
          <input
            value={filtro}
            onChange={(event) => setFiltro(event.target.value)}
            placeholder="Pesquisar profissional ou consultório"
          />
        </label>

        <button
          type="button"
          className="consultorio-nova-agenda"
          onClick={() => navigate(`/agendamentos/${setor}?nova=1`)}
        >
          <Plus size={15} /> Nova agenda
        </button>

        <button
          type="button"
          className="consultorio-arquivadas"
          onClick={() => navigate(`/agendamentos/${setor}?arquivadas=1`)}
        >
          <Archive size={15} /> Ver arquivadas
        </button>
      </section>

      {erro && (
        <div className="consultorio-erro">
          {erro}
          <button type="button" onClick={() => setErro('')}><X size={15} /></button>
        </div>
      )}

      <section className="consultorio-grade-wrap">
        {carregando ? (
          <div className="consultorio-vazio">Carregando consultórios…</div>
        ) : (
          <div className="consultorio-grade">
            <div className="consultorio-grade-canto">Consultórios</div>

            {DIAS.map(([chave, titulo]) => (
              <div className="consultorio-grade-dia" key={chave}>
                {titulo}
                <button type="button" className="consultorio-add-dia" title={`Agendar na ${titulo}`} onClick={() => abrirNovaRapida(chave)}><Plus size={14} /></button>
              </div>
            ))}

            {consultorios.map((consultorio) => (
              <React.Fragment key={consultorio.id}>
                <div className="consultorio-nome consultorio-nome-sala">
                  <span className="consultorio-sala">
                    <small>Sala</small>
                    <strong>{consultorio.ordem || consultorio.id}</strong>
                  </span>
                  {consultorio.agendas.length > 0 && (
                    <span className="consultorio-sala-total">
                      {consultorio.agendas.length} {consultorio.agendas.length === 1 ? 'Agenda Cadastrada' : 'Agendas Cadastradas'}
                    </span>
                  )}
                </div>

                {DIAS.map(([chave]) => {
                  const agendasDoDia = consultorio.agendas.filter((agenda) => {
                    const data = dias.find((item) => item.iso === String(agenda.data).slice(0, 10));
                    return data?.semana === chave;
                  });

                  return (
                    <div className="consultorio-celula" key={`${consultorio.id}-${chave}`}>
                      {agendasDoDia.map((agenda) => (
                        <AgendaBloco
                          key={agenda.id}
                          agenda={agenda}
                          setor={setor}
                          onAbrir={abrirAgenda}
                        />
                      ))}
                      <button type="button" className="consultorio-add-celula" title="Agendar neste consultório" onClick={() => abrirNovaRapida(chave, consultorio)}><Plus size={15} /></button>
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        )}
      </section>

      {consultorioVinculo && (
        <div className="consultorio-modal-fundo">
          <form className="consultorio-modal" onSubmit={salvarVinculoMedico}>
            <header>
              <div>
                <span className="consultorio-kicker">Organização</span>
                <h2>Atrelar médico</h2>
              </div>

              <button
                type="button"
                className="consultorio-fechar"
                onClick={() => setConsultorioVinculo(null)}
              >
                <X size={18} />
              </button>
            </header>

            <p className="consultorio-modal-explicacao">
              As agendas existentes desse médico serão exibidas em
              <strong> {consultorioVinculo.nome}</strong>.
            </p>

            <label>
              Médico
              <select
                value={medicoVinculo}
                onChange={(event) => setMedicoVinculo(event.target.value)}
                required
              >
                <option value="">Selecione o médico</option>
                {medicos.map((medico) => (
                  <option key={medico.id} value={medico.id}>
                    {medico.nome}
                  </option>
                ))}
              </select>
            </label>

            <footer>
              <button
                type="button"
                className="consultorio-button consultorio-button-light"
                onClick={() => setConsultorioVinculo(null)}
              >
                Cancelar
              </button>

              <button
                type="submit"
                className="consultorio-button consultorio-button-primary"
                disabled={salvandoVinculo}
              >
                {salvandoVinculo ? 'Salvando…' : 'Atrelar médico'}
              </button>
            </footer>
          </form>
        </div>
      )}

      {novaRapida && (
        <div className="consultorio-modal-fundo" onMouseDown={(e) => { if (e.target === e.currentTarget) fecharNovaRapida(); }}>
          <form className="consultorio-modal consultorio-modal-rapida" onSubmit={salvarNovaRapida}>
            <header>
              <div>
                <span className="consultorio-kicker">Nova agenda · {(DIAS.find(([c]) => c === novaRapida.dia) || [])[1]}</span>
                <h2>Agendar profissional</h2>
              </div>
              <button type="button" className="consultorio-fechar" onClick={fecharNovaRapida}><X size={18} /></button>
            </header>

            <label>
              Profissional
              <select value={formRapida.medicoId} onChange={(e) => { setConflitos([]); setFormRapida((f) => ({ ...f, medicoId: e.target.value })); }} required>
                <option value="">Selecione o profissional</option>
                {medicos.map((medico) => <option key={medico.id} value={medico.id}>{medico.nome}</option>)}
              </select>
            </label>

            <label>
              Consultório
              <select value={formRapida.consultorioId} onChange={(e) => { setConflitos([]); setFormRapida((f) => ({ ...f, consultorioId: e.target.value })); }} required>
                <option value="">Selecione o consultório</option>
                {(dados.consultorios || []).map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </label>

            <div className="consultorio-rapida-datas">
              <span>Datas em {formatarMes(mes)}</span>
              <div>
                {datasDoDiaRapida.length ? datasDoDiaRapida.map((iso) => (
                  <button key={iso} type="button" className={`consultorio-data-chip${formRapida.datas.includes(iso) ? ' is-selected' : ''}`} onClick={() => alternarDataRapida(iso)}>
                    {iso.slice(8, 10)}/{iso.slice(5, 7)}
                  </button>
                )) : <small>Nenhuma data desse dia no mês.</small>}
              </div>
            </div>

            <div className="consultorio-rapida-horas">
              <label>Início<HoraInput24 value={formRapida.inicio} onChange={(valor) => { setConflitos([]); setFormRapida((f) => ({ ...f, inicio: valor })); }} /></label>
              <label>Fim<HoraInput24 value={formRapida.fim} onChange={(valor) => { setConflitos([]); setFormRapida((f) => ({ ...f, fim: valor })); }} /></label>
              <label>Intervalo
                <select value={formRapida.intervalo} onChange={(e) => setFormRapida((f) => ({ ...f, intervalo: Number(e.target.value) }))}>
                  {[10, 15, 20, 30, 40, 60].map((n) => <option key={n} value={n}>{n} min</option>)}
                </select>
              </label>
            </div>

            {conflitos.length > 0 && (
              <div className="consultorio-conflitos">
                <strong>Horário ocupado — nenhuma agenda foi criada:</strong>
                <ul>
                  {conflitos.map((c, i) => <li key={i}>{c.data.slice(8, 10)}/{c.data.slice(5, 7)} · {c.motivo}: {c.quem} ({c.horario}){c.local}</li>)}
                </ul>
              </div>
            )}
            {erroRapida && <div className="consultorio-conflitos">{erroRapida}</div>}

            <footer>
              <button type="button" className="consultorio-button consultorio-button-light" onClick={fecharNovaRapida} disabled={salvandoRapida}>Cancelar</button>
              <button type="submit" className="consultorio-button consultorio-button-primary" disabled={salvandoRapida}>
                {salvandoRapida ? 'Verificando e salvando…' : (formRapida.datas.length > 1 ? `Criar ${formRapida.datas.length} agendas` : 'Criar agenda')}
              </button>
            </footer>
          </form>
        </div>
      )}

      {novoConsultorio && (
        <div className="consultorio-modal-fundo">
          <form className="consultorio-modal" onSubmit={salvarConsultorio}>
            <header>
              <div>
                <span className="consultorio-kicker">Organização</span>
                <h2>Novo consultório</h2>
              </div>

              <button
                type="button"
                className="consultorio-fechar"
                onClick={() => setNovoConsultorio(false)}
              >
                <X size={18} />
              </button>
            </header>

            <label>
              Nome do consultório
              <input
                autoFocus
                value={nomeNovo}
                onChange={(event) => setNomeNovo(event.target.value)}
                placeholder="Ex.: Consultório 4"
                required
              />
            </label>

            <footer>
              <button
                type="button"
                className="consultorio-button consultorio-button-light"
                onClick={() => setNovoConsultorio(false)}
              >
                Cancelar
              </button>

              <button
                type="submit"
                className="consultorio-button consultorio-button-primary"
                disabled={salvando}
              >
                {salvando ? 'Salvando…' : 'Salvar consultório'}
              </button>
            </footer>
          </form>
        </div>
      )}
    </main>
  );
}
